import React, { useMemo } from "react";
import cx from 'classnames';
import styles from './wizard.module.scss';
import { animated, useSprings } from "@react-spring/web";
import { Carousel } from "react-bootstrap";
import { useSelector } from "react-redux";
import _ from "lodash";

interface WizardStepProps {
  name: string;
  className?: string;
  completed: boolean;
  children: React.ReactNode;
}

const WizardStep: React.FC<WizardStepProps> = ({ name, className, children }) => {

  return (
    <div className={styles.wizardStep}>
      {children}
    </div>
  );
}

interface WizardPaneProps {
  className?: string;
  children: React.ReactElement<WizardStepProps>[] | React.ReactElement<WizardStepProps> | React.ReactNode;
  currentStep: number;
  gap?: number;
  onStepChange?: (step: number) => void;
  wideView?: boolean
}

const WizardPane: React.FC<WizardPaneProps> = ({ className, children, currentStep, gap=30, onStepChange, wideView = false }) => {

  const account = useSelector((state: any) => _.get(state, 'accounts.item'));
  const primaryColor = _.get(account, 'theme.primaryColor', '#E51058');

  const steps = useMemo(() => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return {
          name: child.props.name,
          className: child.props.className,
          completed: child.props.completed,
        };
      }
      return false;
    })?.filter(child => !!child) || [];
  }, [children]);

  const [springs] = useSprings(steps.length, (index) => {

    return {
      backgroundColor: index === currentStep ? primaryColor : "rgba(219, 219, 219, 0.50)",
      color: index === currentStep ? "#fff" : "#727272",
      width: index === currentStep ? 300 : 0,
      opacity: index === currentStep ? 1 : 0,
      config: {
        duration: 500,
        mass: 1,
        friction: 14,
        tension: 120,
      }, 
      delay: index === currentStep ? 250 : 0,
    }
  }, [currentStep]);

  const handleStepClick = (index: number) => {
    const step = steps[currentStep];
    if (!step.completed) {
      return;
    }
    
    if (onStepChange) {
      onStepChange(index);
    }
  }

  return (
    <div className={cx(styles.wizard, className, {
      [styles.wideView] : wideView
    })}>
      <div className={cx(styles.steps, 'wizard-steps')} style={{paddingLeft: gap, paddingRight: gap}}>
        {springs.map(({backgroundColor, color, width, opacity}, index) => {
          const step = steps[index];
          return (
            <React.Fragment key={index}>
              <div
                key={index}
                className={cx(styles.step, {
                  [styles.active]: index === currentStep,
                })}
                onClick={() => {
                  handleStepClick(index);
                }}
              >
                <animated.div
                  style={{ backgroundColor, color }}
                  className={styles.circle}
                >
                  {index + 1}
                </animated.div>
                <animated.div
                  style={{ maxWidth: width, opacity }}
                  className={cx(styles.name, step.className)}
                >
                  <span>{step.name}</span>
                </animated.div>
              </div>
              {index !== steps.length - 1 && (
                <i
                  className={cx(styles.divider, 'wizard_step_divider', "paperflite-up-arrow")}
                ></i>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <Carousel className="wizard-carousel" activeIndex={currentStep} controls={false} indicators={false}>
        {React.Children.map(children, (child, index) => {
          return (
            <Carousel.Item key={index} style={{paddingLeft: gap, paddingRight: gap}}>
              {child}
            </Carousel.Item>
          );
        })}
      </Carousel>
    </div>
  );
}

const Wizard = {
  Pane: WizardPane,
  Step: WizardStep
}

export default Wizard;